import { FC, useState, useEffect } from 'react';
import reactHtmlParser from 'react-html-parser';
import { useGetCustomNavTitle } from 'hooks/useGetCustomNavTitle';
import { RouteKeyConstants } from 'constants/routeKeyConstants';
import PageHeader from 'components/PageHeader/PageHeader';
import { NotificationContextProvider } from 'context/NotificationContext';
import BenefitGuideRefreshChannel from 'modules/clients/UPClient/channels/BenefitGuideRefreshChannel';
import BenefitGuideNotificationsChannel from 'modules/clients/UPClient/channels/BenefitGuideNotificationsChannel';
import { ReactComponent as DisclosuresIcon } from 'images/pageHeaderIcons/disclosures.svg';
import AddDisclosures from 'modules/benefitsWellness/components/AddDisclosures/AddDisclosures';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import {
  getDisclosureTemplate,
  getRichMedia,
} from 'modules/auth/services/BenguideService';
import { getCurrentRichMedia } from 'modules/resources/slices/resourcesSlice';
import { SectionName } from 'modules/home/constants';
import EditableHotspot from 'components/EditableHotspot/EditableHotspot';
import useBenguide from 'hooks/benguide';
import DisclosuresTextEditor from 'modules/resources/components/DisclosuresTextEditor/DisclosuresTextEditor';
import NoDisclosureView from 'components/NoDisclosureView/NoDisclosureView';
import FooterTextSection from 'components/FooterTextSection';
import styles from './disclosures.module.less';

const Disclosures: FC = () => {
  const customNavigationTitle = useGetCustomNavTitle({
    key: RouteKeyConstants.DISCLOSURES as keyof typeof RouteKeyConstants,
  });

  const [isSaveDisable, setSaveDisable] = useState<boolean>(false);
  const [disclosureContent, setDisclosureContent] = useState<string>('');
  const [defaultTemplate, setDefaultTemplate] = useState<string>('');
  const [showEditorModal, setShowEditorModal] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [originalContent, setOriginalContent] = useState<string>('');
  const [tempEditorContent, setTempEditorContent] = useState<string>('');

  const { masterId, isEditMode, urlHash } = useBenguide();
  const dispatch = useAppDispatch();

  const organizationId = useAppSelector((state) => state.benguide.data?.orgId);

  // Helper function to check if HTML content has visible text/content
  const hasVisibleContent = (html: string): boolean => {
    if (!html) return false;

    // Create a temporary div to decode HTML entities and extract text content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Get the text content, which automatically handles HTML entities
    const textContent = tempDiv.textContent || '';

    // Normalize whitespace and trim
    const normalizedText = textContent.replace(/\s+/g, ' ').trim();

    // Check if there's any visible text content left
    return normalizedText.length > 0;
  };

  useEffect(() => {
    const fetchDisclosureContent = async () => {
      if (!masterId) return;
      setLoading(true);
      try {
        const response = await getRichMedia(
          masterId,
          SectionName.DISCLOSURES_TEXT
        );
        if (response?.data?.content) {
          setDisclosureContent(response.data.content);
        } else {
          // If no disclosure content found, fetch default template
          if (organizationId) {
            try {
              const templateResponse = await getDisclosureTemplate(
                organizationId
              );
              if (templateResponse?.data) {
                setDefaultTemplate(templateResponse.data);
                setDisclosureContent(templateResponse.data);
              }
            } catch (error) {
              console.error('Error fetching default template:', error);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching disclosure content:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDisclosureContent();
  }, [masterId, organizationId]);

  // Function to handle content update after successful save
  const handleDisclosureContentUpdate = (content: string) => {
    setDisclosureContent(content);
  };

  // Function to handle template update after saving as default
  const handleTemplateUpdate = (content: string) => {
    setDefaultTemplate(content);
  };

  return (
    <NotificationContextProvider isDisableSave={isSaveDisable}>
      <BenefitGuideRefreshChannel />
      <BenefitGuideNotificationsChannel
        setDisableSave={() => setSaveDisable(true)}
      />
      <div className={styles.disclosuresWrapper}>
        <PageHeader
          pageHeaderTitle={customNavigationTitle}
          pageHeaderIcon={<DisclosuresIcon className={styles.iconSize} />}
          customIconStyle={styles.resIcon}
        />

        <>
          {isEditMode && !hasVisibleContent(disclosureContent) && !loading && (
            <AddDisclosures
              title="Add Disclosures"
              onSave={() => setShowEditorModal(true)}
            />
          )}

          <EditableHotspot
            className={
              hasVisibleContent(disclosureContent) ? '' : styles.disabledHotspot
            }
            alwaysVisible={hasVisibleContent(disclosureContent)}
            customModal={(visible: boolean, onClose: Function) => {
              if (visible) {
                setOriginalContent(disclosureContent);
                setTempEditorContent(disclosureContent || '');
              }
              return (
                <DisclosuresTextEditor
                  isOpenModal={visible || showEditorModal}
                  closeModal={() => {
                    setShowEditorModal(false);
                    setTempEditorContent(disclosureContent || '');
                    onClose();
                  }}
                  isResetEnabled={true}
                  editorContent={tempEditorContent}
                  benguideId={masterId}
                  defaultContent={defaultTemplate || disclosureContent}
                  onCreate={(content: string) => {
                    handleDisclosureContentUpdate(content);

                    if (urlHash) {
                      dispatch(
                        getCurrentRichMedia(
                          masterId,
                          SectionName.DISCLOSURES_TEXT
                        )
                      );
                    }
                  }}
                  onTemplateUpdate={handleTemplateUpdate}
                  updateEditorContent={(content: string): void => {
                    setTempEditorContent(content);
                  }}
                  resetToDefault={() => {
                    // Not implemented
                  }}
                  resetToCurrentValue={() => {
                    setTempEditorContent(originalContent);
                  }}
                />
              );
            }}
          >
            <div className={styles.resourceContent}>
              {reactHtmlParser(disclosureContent)}
            </div>
          </EditableHotspot>
        </>

        {!isEditMode && !hasVisibleContent(disclosureContent) && !loading && (
          <NoDisclosureView />
        )}
        <FooterTextSection masterId={masterId} isEditMode={isEditMode} />
      </div>
    </NotificationContextProvider>
  );
};

export default Disclosures;
